/** Whole fruit PNGs for fruit-ninja-without-score (centered draw, no emoji). */
export const REMOTE_FRUIT_WHOLE_PNG = [
  '/media/watermelon_whole.png',
  '/media/apple_whole.png',
  '/media/peach_whole.png',
  '/media/pineapple_whole.png',
  '/media/strawberry_whole.png',
  '/media/kiwi_whole.png',
] as const;

export const REMOTE_BOMB_PNG = '/media/bomb.png';

/** Order matches REMOTE_FRUIT_WHOLE_PNG / juice colors. */
export const FRUIT_IMAGE_KEYS = [
  'watermelon',
  'apple',
  'peach',
  'pineapple',
  'strawberry',
  'kiwi',
] as const;

export const FRUIT_IMAGE_TO_PNG: Readonly<Record<string, string>> = {
  apple: '/media/apple_whole.png',
  peach: '/media/peach_whole.png',
  pineapple: '/media/pineapple_whole.png',
  strawberry: '/media/strawberry_whole.png',
  watermelon: '/media/watermelon_whole.png',
  kiwi: '/media/kiwi_whole.png',
};

/** Slice halves for fruit-ninja-without-score (dedicated assets, not clipped whole PNG). */
export const FRUIT_IMAGE_TO_HALF_PNG: Readonly<Record<string, string>> = {
  apple: '/media/apple_half.png',
  peach: '/media/peach_half.png',
  pineapple: '/media/pineapple_half.png',
  strawberry: '/media/strawberry_half.png',
  watermelon: '/media/watermelon_half.png',
  kiwi: '/media/kiwi_half.png',
};

/** Juice splash tint per sprite index (matches FRUIT_TYPES vibe). */
export const REMOTE_FRUIT_JUICE_COLOR: readonly string[] = [
  '#ff6688',
  '#ff4444',
  '#ffaa77',
  '#ffee55',
  '#ff7788',
  '#88cc44',
];

export function remoteSpriteIndexForSlot(slotIndex: number): number {
  return slotIndex % REMOTE_FRUIT_WHOLE_PNG.length;
}

export function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

export function resolveRemoteFruitPng(
  image: string | null | undefined,
  isBomb: boolean,
  slotIndex: number,
): string {
  if (isBomb) return REMOTE_BOMB_PNG;
  const key = String(image ?? 'watermelon').toLowerCase().trim();
  return FRUIT_IMAGE_TO_PNG[key] ?? REMOTE_FRUIT_WHOLE_PNG[remoteSpriteIndexForSlot(slotIndex)]!;
}

export function resolveRemoteFruitHalfPng(image: string | null | undefined, slotIndex: number): string {
  const key = String(image ?? 'watermelon').toLowerCase().trim();
  const fromKey = FRUIT_IMAGE_TO_HALF_PNG[key];
  if (fromKey) return fromKey;
  const fallbackKey = FRUIT_IMAGE_KEYS[remoteSpriteIndexForSlot(slotIndex)]!;
  return FRUIT_IMAGE_TO_HALF_PNG[fallbackKey]!;
}

/** True when `fruitImageSrc` is a pre-authored half sprite (draw full bitmap, no top-half clip). */
export function isRemoteHalfPngPath(src: string): boolean {
  return /_half\.png$/i.test(src);
}

export function spriteIndexFromImageKey(image: string | null | undefined, slotIndex: number): number {
  const key = String(image ?? 'watermelon').toLowerCase().trim();
  const idx = FRUIT_IMAGE_KEYS.indexOf(key as (typeof FRUIT_IMAGE_KEYS)[number]);
  if (idx >= 0) return idx;
  return remoteSpriteIndexForSlot(slotIndex);
}

export function juiceColorForRemoteFruit(
  image: string | null | undefined,
  isBomb: boolean,
  slotIndex: number,
): string {
  if (isBomb) return '#ff6600';
  const i = spriteIndexFromImageKey(image, slotIndex);
  return REMOTE_FRUIT_JUICE_COLOR[i % REMOTE_FRUIT_JUICE_COLOR.length]!;
}

export const REMOTE_FRUIT_PRELOAD_URLS: readonly string[] = [
  ...REMOTE_FRUIT_WHOLE_PNG,
  ...Object.values(FRUIT_IMAGE_TO_HALF_PNG),
  REMOTE_BOMB_PNG,
];
